import { useState } from 'react';
import {
  ArrowUpRight,
  ArrowDownRight,
  Repeat,
  CreditCard,
  MoreVertical,
  Pencil,
  Trash2,
  Check,
  Clock,
  ChevronDown,
} from 'lucide-react';
import { useAccounts } from '@hooks/useAccounts';
import { useCategories } from '@hooks/useCategories';
import Card from '@components/common/Card';
import Badge from '@components/common/Badge';
import Button from '@components/common/Button';
import EmptyState from '@components/common/EmptyState';
import { formatCurrency, formatDate } from '@utils/formatters';
import { clsx } from 'clsx';

const UnifiedTransactionList = ({
  items,
  onEdit,
  onDelete,
  onPayInstallment,
  onLaunchRecurrence,
  selectedDate,
  groupByDate = true,
}) => {
  const { accounts } = useAccounts();
  const { categories } = useCategories();
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  const getAccountName = (accountId) => {
    const account = accounts.find(a => a.accountId === accountId);
    return account ? `${account.icon} ${account.name}` : '';
  };

  const getCategoryInfo = (categoryId) => {
    const category = categories.find(c => c.categoryId === categoryId);
    return category || { name: 'Sem categoria', icon: '📁', color: '#6B7280' };
  };

  const getSourceIcon = (source) => {
    switch (source) {
      case 'recurrence':
        return <Repeat size={14} className="text-purple-500" />;
      case 'installment':
        return <CreditCard size={14} className="text-orange-500" />;
      default:
        return null;
    }
  };

  const getSourceLabel = (item) => {
    switch (item.source) {
      case 'recurrence':
        return 'Recorrência';
      case 'installment':
        return `Parcela ${item.installmentNumber}/${item.totalInstallments}`;
      default:
        return null;
    }
  };

  // Agrupar por data
  const groupedItems = groupByDate
    ? items.reduce((groups, item) => {
        const date = item.date;
        if (!groups[date]) {
          groups[date] = [];
        }
        groups[date].push(item);
        return groups;
      }, {})
    : { all: items };

  const sortedDates = Object.keys(groupedItems).sort((a, b) => b.localeCompare(a));

  const toggleGroup = (date) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(date)) {
      newExpanded.delete(date);
    } else {
      newExpanded.add(date);
    }
    setExpandedGroups(newExpanded);
  };

  // Por padrão, expandir o grupo do dia selecionado ou hoje
  const today = new Date().toISOString().split('T')[0];
  const defaultExpanded = selectedDate || today;

  if (items.length === 0) {
    return (
      <EmptyState
        icon={<Clock size={32} />}
        title="Nenhuma movimentação"
        description="Não há transações no período selecionado"
      />
    );
  }

  const [launchingId, setLaunchingId] = useState(null);

  const handleLaunch = async (item) => {
    if (launchingId) return;
    setLaunchingId(item.id);
    try {
      await onLaunchRecurrence?.(item);
    } finally {
      setLaunchingId(null);
    }
  };

  const TransactionRow = ({ item }) => {
    const category = getCategoryInfo(item.categoryId);
    const isPending = item.status === 'pending';
    const isIncome = item.type === 'income';

    return (
      <div
        className={clsx(
          'flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group',
          isPending && 'opacity-75'
        )}
      >
        <div className="flex items-center gap-4">
          {/* Ícone */}
          <div className={clsx(
            'w-10 h-10 rounded-lg flex items-center justify-center relative',
            isIncome
              ? 'bg-green-100 dark:bg-green-900/30'
              : 'bg-red-100 dark:bg-red-900/30'
          )}>
            {isIncome ? (
              <ArrowUpRight className="text-green-600" size={20} />
            ) : (
              <ArrowDownRight className="text-red-600" size={20} />
            )}
            {isPending && (
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-400 rounded-full flex items-center justify-center">
                <Clock size={10} className="text-yellow-800" />
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <div className="flex items-center gap-2">
              <p className={clsx(
                'font-medium',
                isPending ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-white'
              )}>
                {item.description}
              </p>
              {getSourceIcon(item.source)}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <span
                className="text-xs px-1.5 py-0.5 rounded"
                style={{ backgroundColor: `${category.color}20`, color: category.color }}
              >
                {category.icon} {category.name}
              </span>
              <span className="text-xs text-gray-400">
                {getAccountName(item.accountId)}
              </span>
              {getSourceLabel(item) && (
                <Badge variant="default" size="sm">
                  {getSourceLabel(item)}
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Valor e Ações */}
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className={clsx(
              'font-semibold',
              isIncome ? 'text-green-600' : 'text-red-600',
              isPending && 'opacity-75'
            )}>
              {isIncome ? '+' : ''}{formatCurrency(item.amount)}
            </p>
            {isPending && (
              <p className="text-xs text-yellow-600 dark:text-yellow-500">
                Previsto
              </p>
            )}
          </div>

          {/* Ações */}
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {item.source === 'transaction' && (
              <>
                <button
                  onClick={() => onEdit?.(item)}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <Pencil size={16} className="text-gray-400" />
                </button>
                <button
                  onClick={() => onDelete?.(item)}
                  className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  <Trash2 size={16} className="text-red-500" />
                </button>
              </>
            )}
            {item.source === 'installment' && isPending && (
              <Button
                size="sm"
                onClick={() => onPayInstallment?.(item)}
              >
                <Check size={14} className="mr-1" />
                Pagar
              </Button>
            )}
            {item.source === 'recurrence' && isPending && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => handleLaunch(item)}
                isLoading={launchingId === item.id}
                disabled={!!launchingId}
              >
                <Check size={14} className="mr-1" />
                Lançar
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {sortedDates.map((date) => {
        const dateItems = groupedItems[date];
        const isExpanded = expandedGroups.has(date) || date === defaultExpanded || !groupByDate;
        const dayTotal = dateItems.reduce((sum, item) => sum + item.amount, 0);
        const pendingCount = dateItems.filter(i => i.status === 'pending').length;

        return (
          <Card key={date} padding="none" className="overflow-hidden">
            {/* Header do Grupo (Data) */}
            {groupByDate && (
              <button
                onClick={() => toggleGroup(date)}
                className={clsx(
                  'w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors',
                  date === selectedDate && 'bg-primary-50 dark:bg-primary-900/20'
                )}
              >
                <div className="flex items-center gap-3">
                  <div className="text-left">
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {formatDate(date, 'full')}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {dateItems.length} transação(ões)
                      {pendingCount > 0 && ` • ${pendingCount} prevista(s)`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <p className={clsx(
                    'font-semibold',
                    dayTotal >= 0 ? 'text-green-600' : 'text-red-600'
                  )}>
                    {dayTotal >= 0 ? '+' : ''}{formatCurrency(dayTotal)}
                  </p>
                  <ChevronDown
                    size={20}
                    className={clsx(
                      'text-gray-400 transition-transform',
                      isExpanded && 'rotate-180'
                    )}
                  />
                </div>
              </button>
            )}

            {/* Lista de Transações */}
            {isExpanded && (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {dateItems.map((item) => (
                  <TransactionRow key={item.id} item={item} />
                ))}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
};

export default UnifiedTransactionList;
